import { useState } from "react";
import { ThemeProvider } from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import { token } from "../../../../theme";
import computerIcon from "../../../../assets/images/computer_explorer-3.png";
import gearsIcon from "../../../../assets/images/gears_tweakui_a-1.png";
import paintIcon from "../../../../assets/images/paint_old-0.png";
import msnIcon from "../../../../assets/images/msn3-4.png";
import windowsIcon from "../../../../assets/images/windows-0.png";
import {
  AddressField,
  AddressRow,
  CapacityBar,
  ExplorerBody,
  ExplorerContainer,
  GridItem,
  IconGrid,
  MainPane,
  StatusBar,
  StatusPane,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  WebViewIcon,
  WebViewPanel,
  WebViewRule,
  WebViewText,
  WebViewTitle,
} from "../../../../components/Explorer/ExplorerStyles";
import {
  BackGlyph,
  CdIcon,
  FileIcon,
  FloppyIcon,
  HardDriveIcon,
  PropertiesGlyph,
  UpGlyph,
} from "../../../../components/Explorer/icons";
import { PROFILE } from "../../../../config/profile";
import { useDesktop } from "../../utils/DesktopContext";
import { useDialog } from "../../utils/useDialog";
import { getProjectIcon, useOpenProject } from "../../utils/useOpenProject";
import { useSystemWindows } from "../../utils/useSystemWindows";
import { getProjectStats } from "../../utils/projectStats";
import { GAME_APPS, useOpenApp } from "../BackgroundIcons/gameApps";
import { WebProjectItemModel } from "../../utils/types";

export type ComputerPath = "root" | "projects" | "games" | "control";

interface Entry {
  id: string;
  name: string;
  icon: React.ReactNode;
  // side panel content when selected
  details: React.ReactNode;
  open: () => void;
  project?: WebProjectItemModel;
}

const PATH_INFO: Record<
  ComputerPath,
  {
    title: string;
    address: string;
    icon: React.ReactNode;
    parent?: ComputerPath;
  }
> = {
  root: {
    title: "My Computer",
    address: "My Computer",
    icon: <img src={computerIcon} alt="" />,
  },
  projects: {
    title: "Local Disk (C:)",
    address: "C:\\Projects",
    icon: <HardDriveIcon />,
    parent: "root",
  },
  games: {
    title: "Games (D:)",
    address: "D:\\Games",
    icon: <CdIcon />,
    parent: "root",
  },
  control: {
    title: "Control Panel",
    address: "Control Panel",
    icon: <img src={gearsIcon} alt="" />,
    parent: "root",
  },
};

const formatDate = (date: string) => {
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const truncate = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, length).trimEnd()}...` : text;

interface MyComputerProps {
  initialPath?: ComputerPath;
}

const MyComputer = ({ initialPath = "root" }: MyComputerProps) => {
  const [history, setHistory] = useState<ComputerPath[]>([initialPath]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const path = history[history.length - 1];
  const info = PATH_INFO[path];

  const desktop = useDesktop();
  const showDialog = useDialog();
  const openApp = useOpenApp();
  const { openProject, openProjectDetails } = useOpenProject();
  const { openSystemProperties, openDisplayProperties } = useSystemWindows();
  const stats = getProjectStats(desktop.projects);

  const navigate = (next: ComputerPath) => {
    setHistory((prev) => [...prev, next]);
    setSelectedId(null);
  };
  const goBack = () => {
    setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    setSelectedId(null);
  };
  const goUp = () => info.parent && navigate(info.parent);

  const floppyError = () =>
    showDialog({
      windowTitle: "A:\\",
      title: "A:\\ is not accessible.",
      content:
        "The device is not ready. (Is anyone still keeping floppies around?)",
      buttons: [{ label: "Retry", onClick: floppyError }, { label: "Cancel" }],
    });

  // Drive fill level is (loosely) based on how many projects are on it
  const usedPercent = Math.min(92, 18 + stats.total * 5);

  const entriesByPath: Record<ComputerPath, Entry[]> = {
    root: [
      {
        id: "a",
        name: "3½ Floppy (A:)",
        icon: <FloppyIcon />,
        details: (
          <WebViewText>
            1.44MB removable disk. Probably has a copy of SimCity on it.
          </WebViewText>
        ),
        open: floppyError,
      },
      {
        id: "c",
        name: "Local Disk (C:)",
        icon: <HardDriveIcon />,
        details: (
          <>
            <WebViewText>
              <b>Projects</b>:{" "}
              {desktop.loadStatus === "loading"
                ? "scanning..."
                : `${stats.total} installed`}
            </WebViewText>
            <WebViewText>
              Capacity: 4.20GB
              <CapacityBar $used={usedPercent} />
              {usedPercent}% used by side projects
            </WebViewText>
          </>
        ),
        open: () => navigate("projects"),
      },
      {
        id: "d",
        name: "Games (D:)",
        icon: <CdIcon />,
        details: (
          <WebViewText>
            {GAME_APPS.length} games for when you need a break from reading
            about me.
          </WebViewText>
        ),
        open: () => navigate("games"),
      },
      {
        id: "control",
        name: "Control Panel",
        icon: <img src={gearsIcon} alt="" />,
        details: (
          <WebViewText>
            Change the wallpaper, sounds and learn about this system.
          </WebViewText>
        ),
        open: () => navigate("control"),
      },
    ],
    projects: desktop.projects.map((project) => ({
      id: project.id,
      name: project.title,
      project,
      icon: <img src={getProjectIcon(project)} alt="" />,
      details: (
        <>
          {project.important && (
            <WebViewText>
              <b>★ Featured project</b>
            </WebViewText>
          )}
          {project.dateCompleted && (
            <WebViewText>
              <b>Completed:</b> {formatDate(project.dateCompleted)}
            </WebViewText>
          )}
          {project.techStack?.length > 0 && (
            <WebViewText>
              <b>Built with:</b> {project.techStack.join(", ")}
            </WebViewText>
          )}
          {project.description && (
            <WebViewText>{truncate(project.description, 220)}</WebViewText>
          )}
          <WebViewText>
            Double-click to launch it, or hit <b>Properties</b> for screenshots.
          </WebViewText>
        </>
      ),
      open: () => openProject(project),
    })),
    games: GAME_APPS.map((game) => ({
      id: game.name,
      name: game.name,
      icon: <img src={game.iconSrc} alt="" />,
      details: <WebViewText>{game.description}</WebViewText>,
      open: () => openApp(game),
    })),
    control: [
      {
        id: "system",
        name: "System",
        icon: <img src={computerIcon} alt="" />,
        details: (
          <WebViewText>
            About this computer: specs, installed technologies and contact info.
          </WebViewText>
        ),
        open: openSystemProperties,
      },
      {
        id: "display",
        name: "Display",
        icon: <img src={paintIcon} alt="" />,
        details: (
          <WebViewText>
            Change the desktop wallpaper and CRT effect.
          </WebViewText>
        ),
        open: openDisplayProperties,
      },
      {
        id: "sounds",
        name: `Sounds (${desktop.soundOn ? "On" : "Off"})`,
        icon: <img src={msnIcon} alt="" />,
        details: (
          <WebViewText>Toggle the startup chime and system sounds.</WebViewText>
        ),
        open: desktop.toggleSound,
      },
      {
        id: "shutdown",
        name: "Shut Down",
        icon: <img src={windowsIcon} alt="" />,
        details: (
          <WebViewText>
            Turn it off and on again. Replays the boot sequence.
          </WebViewText>
        ),
        open: desktop.shutDown,
      },
    ],
  };

  const entries = entriesByPath[path];
  const selected = entries.find((entry) => entry.id === selectedId);

  return (
    <ThemeProvider theme={token}>
      <ExplorerContainer $width={700} $height={440}>
        <Toolbar>
          <ToolbarButton onClick={goBack} disabled={history.length < 2}>
            <BackGlyph />
            Back
          </ToolbarButton>
          <ToolbarButton onClick={goUp} disabled={!info.parent}>
            <UpGlyph />
            Up
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton
            onClick={() =>
              selected?.project && openProjectDetails(selected.project)
            }
            disabled={!selected?.project}
          >
            <PropertiesGlyph />
            Properties
          </ToolbarButton>
        </Toolbar>
        <AddressRow>
          Address
          <AddressField>
            {info.icon}
            {info.address}
          </AddressField>
        </AddressRow>

        <ExplorerBody>
          <WebViewPanel>
            <WebViewIcon>{selected ? selected.icon : info.icon}</WebViewIcon>
            <WebViewTitle>{selected ? selected.name : info.title}</WebViewTitle>
            <WebViewRule />
            {selected ? (
              selected.details
            ) : path === "root" ? (
              <>
                <WebViewText>
                  Welcome to {PROFILE.name}'s computer. Select an item to view
                  its description, double-click to open it.
                </WebViewText>
                <WebViewText>
                  <b>C:</b> holds every project I've shipped, <b>D:</b> has
                  games and the <b>Control Panel</b> lets you make yourself at
                  home.
                </WebViewText>
              </>
            ) : path === "projects" ? (
              <WebViewText>
                {desktop.loadStatus === "error"
                  ? "Couldn't read the drive. The project server may be down."
                  : `${entries.length} projects, newest first. Select one for details.`}
              </WebViewText>
            ) : (
              <WebViewText>Select an item to view its description.</WebViewText>
            )}
          </WebViewPanel>

          <MainPane onClick={() => setSelectedId(null)}>
            <AnimatePresence mode="wait">
              <motion.div
                key={path}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.15 }}
              >
                <IconGrid>
                  {entries.map((entry) => (
                    <GridItem
                      key={entry.id}
                      $selected={entry.id === selectedId}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(entry.id);
                      }}
                      onDoubleClick={entry.open}
                      onContextMenu={(e) => {
                        if (!entry.project) return;
                        e.preventDefault();
                        openProjectDetails(entry.project);
                      }}
                    >
                      {entry.icon}
                      <span>{entry.name}</span>
                    </GridItem>
                  ))}
                  {path === "projects" && desktop.loadStatus === "loading" && (
                    <GridItem as="div">
                      <FileIcon kind="folder" />
                      <span>Loading...</span>
                    </GridItem>
                  )}
                </IconGrid>
              </motion.div>
            </AnimatePresence>
          </MainPane>
        </ExplorerBody>

        <StatusBar>
          <StatusPane $grow>
            {selected ? `1 object selected` : `${entries.length} object(s)`}
          </StatusPane>
          <StatusPane>
            <span
              style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
            >
              <img
                src={computerIcon}
                alt=""
                style={{ width: 14, height: 14 }}
              />
              My Computer
            </span>
          </StatusPane>
        </StatusBar>
      </ExplorerContainer>
    </ThemeProvider>
  );
};

export default MyComputer;
